/**
 * The web application entry point.
 *
 * The router keeps the selected organization, assessment, and control in the
 * address, so a person can return to the same work after a reload.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  redirect,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  RouterProvider,
} from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { AppShell } from "./app-shell.tsx";
import { parseAuditSearch } from "./lib/audit-tabs.ts";
import { parseDashboardSearch } from "./lib/dashboard.ts";
import "./styles.css";

const rootRoute = createRootRoute({ component: AppShell });

// Every page loads when a person opens it, so the first paint carries the
// shell only. The editor, the diagram canvas, and the assessment workspace
// are the large ones.
const EmployeesPage = lazyRouteComponent(() => import("./routes/employees.tsx"), "EmployeesPage");
const EmployeePortalPage = lazyRouteComponent(() => import("./routes/employee-portal.tsx"), "EmployeePortalPage");
const EmployeeSignInPage = lazyRouteComponent(() => import("./routes/employee-portal.tsx"), "EmployeeSignInPage");
const ApprovalsPage = lazyRouteComponent(() => import("./routes/approvals.tsx"), "ApprovalsPage");
const AssessmentsPage = lazyRouteComponent(() => import("./routes/assessments.tsx"), "AssessmentsPage");
const AuditEngagementPage = lazyRouteComponent(() => import("./routes/audit-engagement.tsx"), "AuditEngagementPage");
const AuditPrepPage = lazyRouteComponent(() => import("./routes/audit-prep.tsx"), "AuditPrepPage");
const RequestsPage = lazyRouteComponent(() => import("./routes/requests.tsx"), "RequestsPage");
const AuditAcceptPage = lazyRouteComponent(() => import("./routes/audit-portal.tsx"), "AuditAcceptPage");
const AuditPortalPage = lazyRouteComponent(() => import("./routes/audit-portal.tsx"), "AuditPortalPage");
const ChangesPage = lazyRouteComponent(() => import("./routes/changes.tsx"), "ChangesPage");
const DocumentEditorPage = lazyRouteComponent(() => import("./routes/document-editor.tsx"), "DocumentEditorPage");
const DocumentsPage = lazyRouteComponent(() => import("./routes/documents.tsx"), "DocumentsPage");
const DataflowsPage = lazyRouteComponent(() => import("./routes/dataflows.tsx"), "DataflowsPage");
const EngagementWorkspace = lazyRouteComponent(() => import("./routes/engagement-workspace.tsx"), "EngagementWorkspace");
const EngagementsPage = lazyRouteComponent(() => import("./routes/engagements.tsx"), "EngagementsPage");
const HipaaPage = lazyRouteComponent(() => import("./routes/hipaa.tsx"), "HipaaPage");
const InventoriesPage = lazyRouteComponent(() => import("./routes/inventories.tsx"), "InventoriesPage");
const ExceptionsPage = lazyRouteComponent(() => import("./routes/exceptions.tsx"), "ExceptionsPage");
const MonitoringPage = lazyRouteComponent(() => import("./routes/monitoring.tsx"), "MonitoringPage");
const RisksPage = lazyRouteComponent(() => import("./routes/risks.tsx"), "RisksPage");
const PlanOfActionPage = lazyRouteComponent(() => import("./routes/plan-of-action.tsx"), "PlanOfActionPage");
const VulnerabilitiesPage = lazyRouteComponent(() => import("./routes/vulnerabilities.tsx"), "VulnerabilitiesPage");
const AuditProcedurePage = lazyRouteComponent(() => import("./routes/audit-procedure.tsx"), "AuditProcedurePage");
const ExternalConnectionsPage = lazyRouteComponent(() => import("./routes/external-connections.tsx"), "ExternalConnectionsPage");
const ExternalConnectionPage = lazyRouteComponent(() => import("./routes/external-connections.tsx"), "ExternalConnectionPage");
const TabletopListPage = lazyRouteComponent(() => import("./routes/tabletop.tsx"), "TabletopListPage");
const TabletopExercisePage = lazyRouteComponent(() => import("./routes/tabletop.tsx"), "TabletopExercisePage");
const ScopePage = lazyRouteComponent(() => import("./routes/scope.tsx"), "ScopePage");
const ControlWorkspace = lazyRouteComponent(() => import("./routes/control-workspace.tsx"), "ControlWorkspace");
const StatementsPage = lazyRouteComponent(() => import("./routes/statements.tsx"), "StatementsPage");
const EvidencePage = lazyRouteComponent(() => import("./routes/evidence.tsx"), "EvidencePage");
const DashboardPage = lazyRouteComponent(() => import("./routes/dashboard.tsx"), "DashboardPage");
const HomePage = lazyRouteComponent(() => import("./routes/dashboard.tsx"), "HomePage");
const OperationsPage = lazyRouteComponent(() => import("./routes/operations.tsx"), "OperationsPage");
const PlatformHealthPage = lazyRouteComponent(() => import("./routes/operations.tsx"), "PlatformHealthPage");
const PlatformAuditLibraryPage = lazyRouteComponent(() => import("./routes/operations.tsx"), "PlatformAuditLibraryPage");
const PlatformElevationPage = lazyRouteComponent(() => import("./routes/operations.tsx"), "PlatformElevationPage");
const PlatformProblemReportsPage = lazyRouteComponent(() => import("./routes/operations.tsx"), "PlatformProblemReportsPage");
const PlatformAccountRequestsPage = lazyRouteComponent(() => import("./routes/operations.tsx"), "PlatformAccountRequestsPage");
const PlatformJournalPage = lazyRouteComponent(() => import("./routes/operations.tsx"), "PlatformJournalPage");
const IntegrationsPage = lazyRouteComponent(() => import("./routes/integrations.tsx"), "IntegrationsPage");
const SampleLibraryPage = lazyRouteComponent(() => import("./routes/sample-library.tsx"), "SampleLibraryPage");
const PlatformBackupsPage = lazyRouteComponent(() => import("./routes/platform-backups.tsx"), "PlatformBackupsPage");
const BackupsPage = lazyRouteComponent(() => import("./routes/backups.tsx"), "BackupsPage");
const IntegrationPage = lazyRouteComponent(() => import("./routes/integrations.tsx"), "IntegrationPage");
const NewOrganizationPage = lazyRouteComponent(() => import("./routes/new-organization.tsx"), "NewOrganizationPage");
const PeoplePage = lazyRouteComponent(() => import("./routes/people.tsx"), "PeoplePage");
const ProvidersPage = lazyRouteComponent(() => import("./routes/providers.tsx"), "ProvidersPage");
const RecoveryDonePage = lazyRouteComponent(() => import("./routes/security-recovery-done.tsx"), "RecoveryDonePage");
const AccountSettingsPage = lazyRouteComponent(() => import("./routes/settings.tsx"), "AccountSettingsPage");
const RolesPage = lazyRouteComponent(() => import("./routes/roles.tsx"), "RolesPage");
const ProgramImportListPage = lazyRouteComponent(() => import("./routes/program-import.tsx"), "ProgramImportListPage");
const ProgramImportWorkspacePage = lazyRouteComponent(() => import("./routes/program-import.tsx"), "ProgramImportWorkspacePage");
const TrashPage = lazyRouteComponent(() => import("./components/trash.tsx"), "TrashPage");
const ProviderCustomersPage = lazyRouteComponent(() => import("./components/provider-access.tsx"), "ProviderCustomersPage");
const SummaryPage = lazyRouteComponent(() => import("./routes/summary.tsx"), "SummaryPage");
const ReportsPage = lazyRouteComponent(() => import("./routes/reports.tsx"), "ReportsPage");
const SettingsPage = lazyRouteComponent(() => import("./routes/settings.tsx"), "SettingsPage");
const SspSetupPage = lazyRouteComponent(() => import("./routes/ssp-builder.tsx"), "SspSetupPage");
const SspBuilderPage = lazyRouteComponent(() => import("./routes/ssp-builder.tsx"), "SspBuilderPage");
const SrmPage = lazyRouteComponent(() => import("./routes/srm.tsx"), "SrmPage");
const TrustPagePage = lazyRouteComponent(() => import("./routes/trust-page.tsx"), "TrustPagePage");
const SecurityPage = lazyRouteComponent(() => import("./routes/security.tsx"), "SecurityPage");
const SecurityPlatformPage = lazyRouteComponent(() => import("./routes/security-platform.tsx"), "SecurityPlatformPage");
const SecurityHelpdeskPage = lazyRouteComponent(() => import("./routes/security-helpdesk.tsx"), "SecurityHelpdeskPage");
const SecurityMePage = lazyRouteComponent(() => import("./routes/security-me.tsx"), "SecurityMePage");

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
  // The home page can render the dashboard, so it accepts the same search.
  validateSearch: parseDashboardSearch,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardPage,
  validateSearch: parseDashboardSearch,
});

// "Your next steps" lives on the dashboard's Overview tab, which is the one
// Overview destination the guide asks for. This path stays as a redirect so a
// link made while it was a separate page still lands in the right place.
const nextStepsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/next-steps",
  validateSearch: (search: Record<string, unknown>) => ({
    program: typeof search.program === "string" ? search.program : undefined,
  }),
  beforeLoad: ({ search }) => {
    throw redirect({
      to: "/dashboard",
      search: { ...(search.program === undefined ? {} : { program: search.program }), tab: "overview" },
    });
  },
});

const assessmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assessments",
  component: AssessmentsPage,
});

const controlsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assessments/$assessmentId/controls",
  component: ControlWorkspace,
  validateSearch: (search: Record<string, unknown>) => ({
    control: typeof search.control === "string" ? search.control : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
    domain: typeof search.domain === "string" ? search.domain : undefined,
    finding: typeof search.finding === "string" ? search.finding : undefined,
  }),
});

// The record kept between assessments, which is where the work happens.
const statementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/statements",
  component: StatementsPage,
});

// What the organization has been asked for. One standing list.
const requestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/requests",
  component: RequestsPage,
});

const evidenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/evidence",
  component: EvidencePage,
});

const trashRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trash",
  component: TrashPage,
});

const providerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/provider",
  component: ProviderCustomersPage,
});

const summaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/summary",
  component: SummaryPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  component: ReportsPage,
});

const newOrganizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/new",
  component: NewOrganizationPage,
});

const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/people",
  component: PeoplePage,
});

const recoveryDoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security/recovery/done",
  component: RecoveryDonePage,
});

const providersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/providers",
  component: ProvidersPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountSettingsPage,
});

const rolesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/roles",
  component: RolesPage,
});

const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approvals",
  component: ApprovalsPage,
});

const documentEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/documents/$documentId",
  component: DocumentEditorPage,
  // The data flow builder keeps its open tab in the address.
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search["tab"] === "string" ? { tab: search["tab"] } : {},
});

const dataflowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dataflows",
  component: DataflowsPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/documents",
  component: DocumentsPage,
});

const sspWizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ssp/new",
  component: SspSetupPage,
  validateSearch: (search: Record<string, unknown>) => ({
    system: typeof search.system === "string" ? search.system : undefined,
    assessment: typeof search.assessment === "string" ? search.assessment : undefined,
    profile: typeof search.profile === "string" ? search.profile : undefined,
  }),
});

const sspBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ssp/drafts/$draftId",
  component: SspBuilderPage,
});

const srmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/srm/$matrixId",
  component: SrmPage,
});

const risksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/risks",
  component: RisksPage,
});

const planOfActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/plan-of-action",
  component: PlanOfActionPage,
});

const vulnerabilitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vulnerabilities",
  component: VulnerabilitiesPage,
});

const auditProcedureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit-procedure",
  validateSearch: parseAuditSearch,
  component: AuditProcedurePage,
});

const externalConnectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/external-connections",
  component: ExternalConnectionsPage,
});

const externalConnectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/external-connections/$connectionId",
  component: ExternalConnectionPage,
});

const tabletopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tabletop",
  component: TabletopListPage,
});

const tabletopExerciseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tabletop/$exerciseId",
  component: TabletopExercisePage,
});

const monitoringRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/monitoring",
  component: MonitoringPage,
});

const changesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/changes",
  component: ChangesPage,
});

const hipaaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hipaa",
  component: HipaaPage,
  validateSearch: (search: Record<string, unknown>) => ({
    assessment: typeof search.assessment === "string" ? search.assessment : undefined,
  }),
});

const operationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations",
  component: OperationsPage,
});

const backupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/backups",
  component: BackupsPage,
});

const platformHealthPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/health",
  component: PlatformHealthPage,
});

const platformAuditLibraryPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/audit-library",
  component: PlatformAuditLibraryPage,
});

const platformElevationPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/elevation",
  component: PlatformElevationPage,
});

const platformProblemReportsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/problem-reports",
  component: PlatformProblemReportsPage,
});

const platformAccountRequestsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/account-requests",
  component: PlatformAccountRequestsPage,
});

const platformJournalPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/journal",
  component: PlatformJournalPage,
});

const sampleLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/sample-library",
  component: SampleLibraryPage,
});

const platformBackupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/backups",
  component: PlatformBackupsPage,
});

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/integrations",
  component: IntegrationsPage,
});

const integrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/integrations/$integrationId",
  component: IntegrationPage,
});

const engagementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engagements",
  component: EngagementsPage,
});

const engagementWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engagements/$engagementId",
  component: EngagementWorkspace,
});

// The countdown and the readiness checks, before the assessors arrive.
const auditPrepRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit-prep",
  component: AuditPrepPage,
});

const auditEngagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit/$engagementId",
  validateSearch: (search: Record<string, unknown>) => ({ area: typeof search["area"] === "string" ? search["area"] : undefined }),
  component: AuditEngagementPage,
});

const auditPortalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit/portal/$engagementId",
  component: AuditPortalPage,
});

const auditAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit/accept",
  validateSearch: (search: Record<string, unknown>) => ({ token: typeof search["token"] === "string" ? search["token"] : undefined }),
  component: AuditAcceptPage,
});

const scopeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scope",
  component: ScopePage,
  validateSearch: (search: Record<string, unknown>) => ({
    system: typeof search.system === "string" ? search.system : undefined,
  }),
});

const trustPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/public-page",
  component: TrustPagePage,
});

const inventoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inventories",
  component: InventoriesPage,
});

const exceptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/exceptions",
  component: ExceptionsPage,
});

const employeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/employees",
  component: EmployeesPage,
});

const employeePortalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/employee",
  component: EmployeePortalPage,
});

const employeeSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/employee/sign-in",
  validateSearch: (search: Record<string, unknown>) => ({ token: typeof search["token"] === "string" ? search["token"] : undefined }),
  component: EmployeeSignInPage,
});

const programImportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/program-imports",
  component: ProgramImportListPage,
});

const programImportWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/program-imports/$id",
  component: ProgramImportWorkspacePage,
  validateSearch: (search: Record<string, unknown>): { step?: string } =>
    typeof search["step"] === "string" ? { step: search["step"] } : {},
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage,
});

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security",
  component: SecurityPage,
});

const securityPlatformRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security/platform",
  component: SecurityPlatformPage,
});

const securityHelpdeskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security/helpdesk",
  component: SecurityHelpdeskPage,
});

const securityMeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security/me",
  component: SecurityMePage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  summaryRoute,
  providerRoute,
  trashRoute,
  dashboardRoute,
  nextStepsRoute,
  assessmentsRoute,
  controlsRoute,
  statementsRoute,
  requestsRoute,
  evidenceRoute,
  reportsRoute,
  approvalsRoute,
  documentsRoute,
  sspWizardRoute,
  sspBuilderRoute,
  srmRoute,
  documentEditorRoute,
  dataflowsRoute,
  risksRoute,
  planOfActionRoute,
  vulnerabilitiesRoute,
  auditProcedureRoute,
  externalConnectionsRoute,
  externalConnectionRoute,
  tabletopRoute,
  tabletopExerciseRoute,
  auditPrepRoute,
  monitoringRoute,
  changesRoute,
  hipaaRoute,
  engagementsRoute,
  engagementWorkspaceRoute,
  auditEngagementRoute,
  auditPortalRoute,
  auditAcceptRoute,
  newOrganizationRoute,
  peopleRoute,
  providersRoute,
  recoveryDoneRoute,
  rolesRoute,
  accountRoute,
  programImportsRoute,
  programImportWorkspaceRoute,
  scopeRoute,
  trustPageRoute,
  operationsRoute,
  platformBackupsRoute,
  sampleLibraryRoute,
  platformHealthPageRoute,
  platformAuditLibraryPageRoute,
  platformElevationPageRoute,
  platformProblemReportsPageRoute,
  platformAccountRequestsPageRoute,
  platformJournalPageRoute,
  integrationsRoute,
  backupsRoute,
  integrationRoute,
  settingsRoute,
  employeesRoute,
  inventoriesRoute,
  exceptionsRoute,
  employeePortalRoute,
  employeeSignInRoute,
  securityRoute,
  securityPlatformRoute,
  securityHelpdeskRoute,
  securityMeRoute,
]);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15_000 },
  },
});

const container = document.getElementById("root");
if (container === null) {
  throw new Error("The page has no root element.");
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
